import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Typography,
  Avatar,
  Dropdown,
  Space,
  Tag,
  message,
  Modal,
  Select,
  Row,
  Col,
  Progress
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  TeamOutlined,
  UnlockOutlined,
  ClockCircleOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function ClassroomManagement() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [students, setStudents] = useState([]);
  const [labs, setLabs] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedLab, setSelectedLab] = useState(null);
  const [allStudents, setAllStudents] = useState([]);

  useEffect(() => {
    fetchClassrooms();
    fetchAllStudents();
  }, []);

  useEffect(() => {
    if (selectedClassroom) {
      fetchClassroomDetails(selectedClassroom);
    }
  }, [selectedClassroom]);

  const fetchClassrooms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/facilitator/classrooms');
      setClassrooms(response.data);
      if (response.data.length > 0 && !selectedClassroom) {
        setSelectedClassroom(response.data[0].id);
      }
    } catch (error) {
      message.error('Failed to load classrooms');
      console.error('Error fetching classrooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassroomDetails = async (classroomId) => {
    try {
      const [studentsRes, labsRes] = await Promise.allSettled([
        api.get(`/facilitator/classrooms/${classroomId}/students`),
        api.get(`/facilitator/classrooms/${classroomId}/labs`)
      ]);

      if (studentsRes.status === 'fulfilled') {
        setStudents(studentsRes.value.data);
      }

      if (labsRes.status === 'fulfilled') {
        setLabs(labsRes.value.data);
      }
    } catch (error) {
      message.error('Failed to load classroom details');
      console.error('Error fetching classroom details:', error);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const response = await api.get('/facilitator/students');
      setAllStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleAssignTraining = async (trainingId, userIds) => {
    try {
      await api.post('/facilitator/classrooms/assign', {
        training_id: trainingId,
        user_ids: userIds
      });
      message.success('Training assigned successfully');
      setAssignModalVisible(false);
      setSelectedStudents([]);
      fetchClassrooms();
      if (selectedClassroom) {
        fetchClassroomDetails(selectedClassroom);
      }
    } catch (error) {
      message.error('Failed to assign training');
      console.error('Error assigning training:', error);
    }
  };

  const handleUnlockLab = async () => {
    if (!selectedLab || selectedStudents.length === 0) {
      message.warning('Please select students and a lab');
      return;
    }

    try {
      await api.post('/facilitator/classrooms/unlock-lab', {
        lab_id: selectedLab,
        user_ids: selectedStudents
      });
      message.success('Lab unlocked successfully');
      setUnlockModalVisible(false);
      setSelectedStudents([]);
      setSelectedLab(null);
      fetchClassroomDetails(selectedClassroom);
    } catch (error) {
      message.error('Failed to unlock lab');
      console.error('Error unlocking lab:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/facilitator/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const studentColumns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{`${record.first_name} ${record.last_name}`}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          assigned: 'blue',
          in_progress: 'orange',
          completed: 'green'
        };
        return <Tag color={colors[status]}>{status.toUpperCase().replace('_', ' ')}</Tag>;
      },
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => (
        <div>
          <Progress
            percent={record.total_labs > 0 ? Math.round((record.completed_labs / record.total_labs) * 100) : 0}
            size="small"
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.completed_labs} / {record.total_labs} labs
          </Text>
        </div>
      ),
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const labColumns = [
    {
      title: 'Lab',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Space>
          <ExperimentOutlined style={{ color: '#faad14' }} />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Unlocked',
      key: 'unlocked',
      render: (_, record) => (
        <Tag color="green">{record.unlocked_count} students</Tag>
      ),
    },
    {
      title: 'Active Sessions',
      dataIndex: 'active_sessions',
      key: 'active_sessions',
      render: (count) => (
        <Tag color={count > 0 ? 'orange' : 'default'}>
          <ClockCircleOutlined /> {count}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<UnlockOutlined />}
          onClick={() => {
            setSelectedLab(record.id);
            setUnlockModalVisible(true);
          }}
        >
          Unlock
        </Button>
      ),
    },
  ];

  const currentClassroom = classrooms.find(c => c.id === selectedClassroom);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/facilitator/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Classroom Management</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Classroom Management</Title>
          <Text type="secondary">Manage student assignments and unlock labs</Text>
        </div>

        {/* Classroom Selector */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Select
                style={{ width: '100%' }}
                placeholder="Select a classroom"
                value={selectedClassroom}
                onChange={setSelectedClassroom}
                size="large"
              >
                {classrooms.map(classroom => (
                  <Select.Option key={classroom.id} value={classroom.id}>
                    {classroom.name} ({classroom.student_count} students, {classroom.active_sessions} active)
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<TeamOutlined />}
                size="large"
                onClick={() => setAssignModalVisible(true)}
              >
                Assign Students
              </Button>
            </Col>
          </Row>

          {currentClassroom && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">{currentClassroom.description}</Text>
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Tag color="blue">{currentClassroom.type.replace('_', ' ')}</Tag>
                  <Tag color="green">{currentClassroom.student_count} students</Tag>
                  <Tag color="orange">{currentClassroom.active_sessions} active sessions</Tag>
                </Space>
              </div>
            </div>
          )}
        </Card>

        {/* Students Table */}
        <Card title="Enrolled Students" style={{ marginBottom: 24 }}>
          <Table
            columns={studentColumns}
            dataSource={students}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>

        {/* Labs Table */}
        <Card title="Labs" extra={
          <Button
            type="primary"
            icon={<UnlockOutlined />}
            onClick={() => setUnlockModalVisible(true)}
            disabled={!selectedClassroom}
          >
            Unlock Lab for Students
          </Button>
        }>
          <Table
            columns={labColumns}
            dataSource={labs}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </Content>

      {/* Assign Training Modal */}
      <Modal
        title="Assign Training to Students"
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedStudents([]);
        }}
        onOk={() => {
          if (selectedClassroom && selectedStudents.length > 0) {
            handleAssignTraining(selectedClassroom, selectedStudents);
          } else {
            message.warning('Please select students');
          }
        }}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Students:</Text>
        </div>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select students to assign"
          value={selectedStudents}
          onChange={setSelectedStudents}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {allStudents.map(student => (
            <Select.Option key={student.id} value={student.id}>
              {student.first_name} {student.last_name} ({student.email})
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Unlock Lab Modal */}
      <Modal
        title="Unlock Lab for Students"
        open={unlockModalVisible}
        onCancel={() => {
          setUnlockModalVisible(false);
          setSelectedStudents([]);
          setSelectedLab(null);
        }}
        onOk={handleUnlockLab}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Lab:</Text>
        </div>
        <Select
          style={{ width: '100%', marginBottom: 16 }}
          placeholder="Select a lab"
          value={selectedLab}
          onChange={setSelectedLab}
        >
          {labs.map(lab => (
            <Select.Option key={lab.id} value={lab.id}>
              {lab.name} ({lab.unlocked_count} already unlocked)
            </Select.Option>
          ))}
        </Select>

        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Students:</Text>
        </div>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select students to unlock lab"
          value={selectedStudents}
          onChange={setSelectedStudents}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {students.map(student => (
            <Select.Option key={student.id} value={student.id}>
              {student.first_name} {student.last_name}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </Layout>
  );
}
